const ACCESS_TOKEN_KEY = 'web_chat_access_token';
const REFRESH_TOKEN_KEY = 'web_chat_refresh_token';

/** Default buffer time in seconds before considering a token expired */
const DEFAULT_EXPIRY_BUFFER_SECONDS = 60;

/**
 * Get the stored access token from localStorage.
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Store the access token in localStorage.
 */
export function setAccessToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

/**
 * Get the stored refresh token from localStorage.
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store the refresh token in localStorage.
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

/**
 * Clear both access and refresh tokens from storage.
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Parse the payload (claims) from a JWT token without verifying the signature.
 * Returns null if the token is invalid or cannot be parsed.
 */
export function parseTokenPayload(token: string | null): Record<string, unknown> | null {
  if (!token) {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    if (!payload) {
      return null;
    }

    // JWT payloads use base64url encoding (RFC 7515)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired, with an optional buffer time.
 * Returns true if expired or invalid.
 */
export function isTokenExpired(
  token: string | null,
  bufferSeconds: number = DEFAULT_EXPIRY_BUFFER_SECONDS,
): boolean {
  const payload = parseTokenPayload(token);
  if (!payload) {
    return true;
  }

  const exp = payload.exp;
  if (typeof exp !== 'number') {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return now >= exp - bufferSeconds;
}

/**
 * Get the expiration date of a JWT token.
 * Returns null if the token is invalid or has no exp claim.
 */
export function getTokenExpiresAt(token: string | null): Date | null {
  const payload = parseTokenPayload(token);
  if (!payload) {
    return null;
  }

  const exp = payload.exp;
  if (typeof exp !== 'number') {
    return null;
  }

  return new Date(exp * 1000);
}
